/**
 * De spelregels van een Krabbelgotchi-gevecht, los van het scherm.
 *
 * Twee beestjes, om de beurt een aanval, tot er een op nul staat. Geen dobbelen
 * in het wilde weg: alles komt uit één zaadje, dus hetzelfde gevecht loopt bij
 * jou en bij je tegenstander precies hetzelfde af. Daarmee kun je het naspelen en
 * kan de ander zien wat er gebeurd is zonder dat we elke klap opslaan.
 *
 * Wat een beestje kan hangt aan hoe je voor hem zorgt. Zijn leeftijd bepaalt zijn
 * niveau, hoe vaak je met hem traint zijn kracht, en hoe vol zijn buik en hoe goed
 * zijn humeur op dít moment zijn bepaalt of hij fit aan de start staat. Een
 * verwaarloosd beestje verliest, en dat is precies de bedoeling: vechten is de
 * beloning voor verzorgen en niet een los spelletje ernaast.
 */

import { K, KOP, METERS, METER_BREED, METER_RIJ, VELD, beeldVan, standVan } from './tamagotchi.js';

const klem = (n, laag, hoog) => Math.max(laag, Math.min(hoog, n));

/** Hoeveel beurten een gevecht hoogstens duurt, zodat het nooit blijft hangen. */
export const MAX_BEURTEN = 30;

/**
 * De vier aanvallen.
 *
 * `kans` is hoe vaak hij raakt, `kracht` hoe hard hij aankomt. Ze zijn tegen
 * elkaar afgewogen: krabbelen mist nooit maar tikt maar zacht, stuiteren doet pijn
 * maar gaat een op de drie keer mis. Krijsen doet zelf niets en haalt de klap van
 * de ander omlaag, dus het loont pas als het gevecht lang duurt.
 */
export const AANVALLEN = [
  { id: 'krabbel', naam: 'Krabbel', kracht: 5, kans: 1, zwak: 0 },
  { id: 'kopstoot', naam: 'Kopstoot', kracht: 9, kans: 0.85, zwak: 0 },
  { id: 'stuiter', naam: 'Stuiter', kracht: 14, kans: 0.65, zwak: 0 },
  { id: 'krijs', naam: 'Krijs', kracht: 0, kans: 0.9, zwak: 2 }
];

export function aanvalVan(id) {
  return AANVALLEN.find((a) => a.id === id) || AANVALLEN[0];
}

/**
 * Een kleine dobbelsteen die je kunt naspelen.
 *
 * Mulberry32: uit één getal komt een rij die er willekeurig uitziet maar altijd
 * dezelfde is. Zonder dit zou een gevecht bij de ander anders aflopen dan bij jou,
 * en dan is een uitslag niets waard.
 */
export function dobbel(zaad) {
  let t = (zaad >>> 0) || 1;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let x = Math.imul(t ^ (t >>> 15), 1 | t);
    x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x;
    return ((x ^ (x >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Wat een beestje in huis heeft.
 *
 * Niveau uit zijn leeftijd, kracht uit hoe vaak je met hem getraind hebt, en
 * conditie uit hoe hij er nú bij staat. Alle drie hebben een bodem, want een
 * beestje dat kansloos is voordat het begint is geen tegenstander maar een
 * oefenpop.
 */
export function krachtenVan(beest, nu) {
  const st = standVan(beest || {}, nu, 12);
  const dagen = Math.max(0, st.dagen || 0);
  const niveau = klem(1 + Math.floor(dagen), 1, 20);
  const getraind = klem(Number(beest && beest.getraind) || 0, 0, 50);
  // Conditie: het gemiddelde van buik en humeur, van 0 tot 100. Halfvol is
  // ongeveer normaal; helemaal leeg halveert wat hij uitdeelt.
  const conditie = klem(((st.honger || 0) + (st.blij || 0)) / 2, 0, 100);
  return {
    niveau,
    hp: 28 + niveau * 5 + Math.floor(getraind / 2),
    aanval: 6 + Math.floor(niveau * 0.8) + Math.floor(getraind / 3),
    verdediging: 5 + Math.floor(niveau * 0.6) + Math.floor(getraind / 4),
    conditie
  };
}

/**
 * Hoeveel een klap aankomt.
 *
 * Dezelfde vorm als in de spelletjes waar dit vandaan komt: niveau maakt het
 * verschil groter, verdediging haalt het omlaag, en er zit een kleine wiebel op
 * zodat twee dezelfde klappen niet exact hetzelfde doen. Altijd minstens één,
 * anders kan een gevecht stilvallen.
 */
export function schadeVan(aanvaller, verdediger, aanval, wiebel, zwakker) {
  if (!aanval.kracht) return 0;
  const aanvalWaarde = Math.max(1, aanvaller.aanval - (zwakker || 0));
  const conditie = 0.5 + (aanvaller.conditie / 100) * 0.5;
  const basis = ((2 * aanvaller.niveau) / 5 + 2) * aanval.kracht * aanvalWaarde
    / Math.max(1, verdediger.verdediging * 4);
  const rauw = Math.max(1, Math.floor(basis * conditie * (0.85 + wiebel * 0.3)));
  /**
   * Geen klap haalt er meer dan een kwart af.
   *
   * Zonder dit plafond was een gevecht tussen niveau 1 en niveau 5 na twee beurten
   * voorbij: zestien van de vierentwintig in één tik. Het niveauverschil telt aan
   * beide kanten mee (harder slaan én beter incasseren) en dat vermenigvuldigt.
   * Met dit plafond duurt het minstens vier beurten, blijft het verschil groot
   * genoeg om te merken, en heb je iets te kiezen in plaats van iets te ondergaan.
   */
  const plafond = Math.max(1, Math.ceil((verdediger.maxHp || verdediger.hp || 20) * 0.25));
  return Math.min(rauw, plafond);
}

/**
 * Een vers gevecht.
 *
 * `mij` en `hem` zijn beestjes. Wie er in een ronde het eerst slaat hangt af van de
 * conditie: goed verzorgd betekent eerst slaan, en dat is elke ronde opnieuw zo.
 */
export function nieuwGevecht(mij, hem, nu, zaad, mijEerst = true) {
  const a = krachtenVan(mij, nu);
  const b = krachtenVan(hem, nu);
  return {
    zaad: (zaad >>> 0) || 1,
    /**
     * Wie er bij gelijke conditie voorgaat.
     *
     * Dit moet van buiten komen, want `mij` is aan elke kant een ánder beestje.
     * Stonden twee beestjes er precies even goed voor, dan zette elke browser
     * zichzelf vooraan, en dan sloeg de een de ander om terwijl die aan de andere
     * kant nog terugsloeg: twee verslagen, twee winnaars, één potje. De uitdager
     * gaat voor, en dat betekent aan beide kanten hetzelfde.
     */
    mijEerst: mijEerst !== false,
    beurt: 0,
    mij: { ...a, hp: a.hp, maxHp: a.hp, zwak: 0 },
    hem: { ...b, hp: b.hp, maxHp: b.hp, zwak: 0 },
    log: [],
    klaar: false,
    gewonnen: null
  };
}

/** Wie er wint, of null zolang ze allebei nog staan. */
export function winnaar(g) {
  if (!g) return null;
  if (g.hem.hp <= 0 && g.mij.hp <= 0) return 'gelijk';
  if (g.hem.hp <= 0) return 'mij';
  if (g.mij.hp <= 0) return 'hem';
  if (g.beurt >= MAX_BEURTEN) {
    // Op is op: wie er relatief het beste voor staat wint. Gelijk blijft gelijk.
    const a = g.mij.hp / g.mij.maxHp;
    const b = g.hem.hp / g.hem.maxHp;
    return a === b ? 'gelijk' : a > b ? 'mij' : 'hem';
  }
  return null;
}

/**
 * Eén ronde: allebei kiezen, en dan gaat het los.
 *
 * Allebei tegelijk en niet om de beurt. Zo zit niemand te wachten tot de ander iets
 * doet, en kun je je keuze niet laten afhangen van wat de ander net deed. Wie het
 * eerst slaat hangt af van de conditie; ligt de ander daarmee om, dan komt die niet
 * meer aan slaan toe.
 *
 * Levert een nieuw gevecht op en raakt het oude niet aan. Daardoor kan elke kant het
 * gevecht narekenen uit het zaadje en de rij zetten, en komen ze op precies hetzelfde
 * uit.
 */
export function doeBeurt(g, aanvalA, aanvalB) {
  if (!g || g.klaar) return g;
  const rng = dobbel(g.zaad + g.beurt * 7919);
  const na = {
    ...g,
    beurt: g.beurt + 1,
    mij: { ...g.mij },
    hem: { ...g.hem },
    log: g.log.slice()
  };

  const slag = (van, op, aanval, naam) => {
    if (na[van].hp <= 0) return;
    if (rng() >= aanval.kans) { na.log.push(naam + ' mist.'); return; }
    if (aanval.zwak) {
      na[op].zwak = Math.min(6, na[op].zwak + aanval.zwak);
      na.log.push(naam + ' krijst; de ander slaat zachter.');
      return;
    }
    const schade = schadeVan(na[van], na[op], aanval, rng(), na[van].zwak);
    na[op].hp = Math.max(0, na[op].hp - schade);
    na.log.push(naam + ' doet ' + aanval.naam.toLowerCase() + ' voor ' + schade + '.');
  };

  const a = { van: 'mij', op: 'hem', aanval: aanvalVan(aanvalA), naam: 'Jij' };
  const b = { van: 'hem', op: 'mij', aanval: aanvalVan(aanvalB), naam: 'De ander' };
  const gelijk = na.mij.conditie === na.hem.conditie;
  const ikEerst = gelijk ? na.mijEerst !== false : na.mij.conditie > na.hem.conditie;
  (ikEerst ? [a, b] : [b, a])
    .forEach((z) => slag(z.van, z.op, z.aanval, z.naam));

  const uit = winnaar(na);
  if (uit) { na.klaar = true; na.gewonnen = uit; }
  return na;
}

/**
 * Een gevecht narekenen uit het zaadje en de rij zetten.
 *
 * Dit is wat de twee kanten gelijk houdt: op de server staat alleen wie wat koos, en
 * allebei de browsers rekenen daar hetzelfde gevecht uit. Zou de stand zelf op de
 * server staan, dan won de laatste schrijver en niet de beste speler.
 *
 * `zetten` is "kopstoot>krabbel,stuiter>krijs": per ronde de keuze van de uitdager
 * en die van de verdediger.
 */
export function speelNa(mij, hem, nu, zaad, zetten, mijEerst = true) {
  let g = nieuwGevecht(mij, hem, nu, zaad, mijEerst);
  String(zetten || '').split(',').filter(Boolean).forEach((ronde) => {
    const [x, y] = ronde.split('>');
    if (x && y) g = doeBeurt(g, x, y);
  });
  return g;
}

/** De rij zetten met er een ronde bij. */
export function metZet(zetten, vanDeUitdager, vanDeVerdediger) {
  const kaal = String(zetten || '').split(',').filter(Boolean);
  kaal.push(vanDeUitdager + '>' + vanDeVerdediger);
  return kaal.join(',');
}

/** Hoeveel rondes er al gespeeld zijn. */
export function aantalRondes(zetten) {
  return String(zetten || '').split(',').filter(Boolean).length;
}

// ---- het gevecht op het schermpje ---------------------------------------

/**
 * Wat je tijdens een gevecht op het schermpje ziet.
 *
 * De tegenstander, en bovenin twee balken: links hoeveel jij nog over hebt, rechts
 * hij. Jezelf zie je niet, net als in de spelletjes waar dit vandaan komt: je kijkt
 * door je eigen ogen en het gaat om wat er tegenover je staat.
 *
 * De balken staan op de plek van de buik en het humeur, want die twee zeggen tijdens
 * een gevecht niets. Onder een kwart worden ze rood, dezelfde afspraak als daar.
 */
export function arenaPixels(gevecht, hemBeest, stukken, tik = 0, nu = Date.now(), eigenaar = '') {
  const stand = standVan(hemBeest || {}, nu, 12);
  const vecht = Object.assign({}, stand, { humeur: 'hongerig', slaapt: false, hoopjes: 0 });
  const soort = Math.max(1, Math.min(6, Math.round(Number(hemBeest && hemBeest.soort)) || 1));
  // Alles onder de kop: het beestje zelf, zonder zijn eigen meters. Met de eigenaar
  // erbij, want elk beestje heeft zijn eigen tekening; zie stukSleutel in
  // lib/tamagotchi.js.
  const uit = beeldVan(vecht, tik, null, stukken, soort, eigenaar).filter((p) => p.y >= KOP);

  const balk = (n, deel) => {
    const plek = METERS[n];
    const vol = Math.round(Math.max(0, Math.min(1, deel)) * METER_BREED);
    const laag = deel < 0.25;
    for (let i = 0; i < METER_BREED; i++) {
      uit.push({
        x: plek.balkX + i, y: METER_RIJ,
        k: i < vol ? (laag ? K.rood : K.groen) : K.grijs
      });
    }
    // Een streepje voor de balk in plaats van een appeltje: dit is geen buik.
    for (let dy = -1; dy <= 1; dy++) uit.push({ x: plek.tekenX + (n ? 2 : 0), y: METER_RIJ + dy, k: K.blauw });
  };
  balk(0, gevecht.mij.hp / gevecht.mij.maxHp);
  balk(1, gevecht.hem.hp / gevecht.hem.maxHp);
  return uit.filter((p) => p.x >= 0 && p.y >= 0 && p.x < VELD && p.y < VELD);
}
